import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, Clock, Lock } from "lucide-react";
import { useState } from "react";
import { useSearch } from "wouter";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { extractPortalErrorCode, portalRpc } from "@/lib/portal-rpc";
import { PortalLayout } from "./portal-layout";
import { PortalShell } from "./portal-shell";

const COMMENT_MAX = 1000;

const SCORE_LABELS: Record<number, string> = {
  1: "Muito ruim",
  2: "Ruim",
  3: "OK",
  4: "Bom",
  5: "Excelente",
};

function CsatInner() {
  const { toast } = useToast();
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState<{ submittedAt: string } | null>(null);
  const [notYetOpen, setNotYetOpen] = useState(false);
  const [closed, setClosed] = useState(false);

  const submit = useMutation({
    ...portalRpc.submitCsat.mutationOptions(),
    onSuccess: (data) => {
      setSubmitted({ submittedAt: data.submittedAt });
    },
    onError: (err) => {
      const code = extractPortalErrorCode(err);
      if (code === "already_submitted") {
        setSubmitted({ submittedAt: new Date().toISOString() });
        return;
      }
      if (code === "not_yet_open") {
        setNotYetOpen(true);
        return;
      }
      if (code === "survey_closed") {
        setClosed(true);
        return;
      }
      toast({ title: "Não foi possível enviar.", variant: "destructive" });
    },
  });

  if (submitted) {
    return <ThanksCard submittedAt={submitted.submittedAt} />;
  }

  if (closed) {
    return <ClosedCard />;
  }

  return (
    <Card className="p-8 space-y-6">
      <header className="space-y-1">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          Check-in de 30 dias
        </div>
        <h1 className="text-2xl font-semibold">Como estão sendo os primeiros 30 dias?</h1>
        <p className="text-sm text-muted-foreground">
          Sua opinião sincera nos ajuda a melhorar o onboarding para os próximos contratados.
        </p>
      </header>

      {notYetOpen && (
        <Alert>
          <Clock className="h-4 w-4" />
          <AlertTitle>Ainda não disponível</AlertTitle>
          <AlertDescription>
            O check-in de 30 dias abre no Dia 30 do seu onboarding. Avisaremos quando for a hora.
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-3">
        <Label className="text-sm font-medium">
          De modo geral, como você avalia sua experiência?
        </Label>
        <RadioGroup
          value={score?.toString() ?? ""}
          onValueChange={(v) => setScore(Number.parseInt(v, 10))}
          className="grid grid-cols-1 sm:grid-cols-5 gap-2"
        >
          {[1, 2, 3, 4, 5].map((n) => (
            <Label
              key={n}
              htmlFor={`csat-${n}`}
              className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border p-3 hover:bg-accent has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
            >
              <RadioGroupItem id={`csat-${n}`} value={n.toString()} />
              <span className="text-lg font-semibold">{n}</span>
              <span className="text-xs text-muted-foreground">{SCORE_LABELS[n]}</span>
            </Label>
          ))}
        </RadioGroup>
      </div>

      <div className="space-y-2">
        <Label htmlFor="csat-comment" className="text-sm font-medium">
          Algo mais que queira adicionar? <span className="text-muted-foreground">(opcional)</span>
        </Label>
        <Textarea
          id="csat-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value.slice(0, COMMENT_MAX))}
          rows={4}
          placeholder="O que está funcionando bem, o que poderia melhorar…"
        />
        <div className="text-xs text-muted-foreground text-right">
          {comment.length} / {COMMENT_MAX}
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          disabled={score === null || submit.isPending || notYetOpen}
          onClick={() => {
            if (score === null) return;
            submit.mutate({ score, comment: comment.trim() ? comment.trim() : null });
          }}
        >
          {submit.isPending ? "Enviando…" : "Enviar"}
        </Button>
      </div>
    </Card>
  );
}

function ClosedCard() {
  return (
    <Card className="p-10 text-center space-y-3">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Lock className="h-6 w-6" />
      </div>
      <h2 className="text-xl font-semibold">Esta pesquisa está encerrada</h2>
      <p className="text-sm text-muted-foreground">
        A janela do check-in de 30 dias encerrou no Dia 37.
      </p>
    </Card>
  );
}

function ThanksCard({ submittedAt }: { submittedAt: string }) {
  const date = new Date(submittedAt);
  const dateLabel = Number.isFinite(date.getTime())
    ? `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`
    : "—";
  return (
    <Card className="p-10 text-center space-y-3">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--sand-step))] text-[hsl(var(--forest-pulse))]">
        <CheckCircle2 className="h-6 w-6" />
      </div>
      <h2 className="text-xl font-semibold">Obrigado pelo seu feedback</h2>
      <p className="text-sm text-muted-foreground">Check-in de 30 dias enviado em {dateLabel}.</p>
    </Card>
  );
}

export default function PortalCsat() {
  const search = useSearch();
  const fromSurveys = new URLSearchParams(search).get("from") === "surveys";
  const backTo = fromSurveys ? { href: "/surveys", label: "Voltar para pesquisas" } : undefined;
  return (
    <PortalShell>
      <PortalLayout backTo={backTo}>
        <CsatInner />
      </PortalLayout>
    </PortalShell>
  );
}

export { ClosedCard, ThanksCard };
